<template>
  <section>
    <b-card-actions
      :show-loading="uploading"
      action-collapse
      :title="$t('Dados da Requisição')"
    >
      <FormulateForm
        v-show="!uploading"
        ref="form"
      >
        <b-row>
          <b-col md="4">
            <e-store-combo
              id="store-combo"
              v-model="purchaseRequisition.storeId"
              name="storeId"
              required
              :is-read-only="readOnly || purchaseRequisition.status === 'Completed' || isSubmitToSupplier"
              :clearable="false"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="group-combo"
              v-model="purchaseRequisition.groupId"
              name="groupId"
              :type="isGroupReadOnly ? 'label' : 'vue-select'"
              :label="$t('Grupo de produto')"
              :placeholder="$t('Todos os grupos')"
              :options="getComboGroups"
              :instruction="
                $t(
                  'Quando informado, somente produtos do grupo selecionado poderão ser incluídos na requisição'
                )
              "
            />
          </b-col>
          <b-col
            v-if="isEdit"
            md="4"
          >
            <FormulateInput
              v-if="!!approvalFlowRoute"
              id="status-label"
              :value="purchaseRequisitionStatus"
              type="label"
              :label="$t('Status')"
              :router-to="approvalFlowRoute"
            />
            <div v-else>
              <FormulateInput
                id="status-label"
                :value="purchaseRequisition.status"
                type="label"
                :label="$t('Status')"
                :options="mxPurchaseRequisitionStatusOptions()"
                is-badge
              />
            </div>
          </b-col>

          <template v-if="purchaseRequisition.status === 'Deleted'">
            <b-col md="2">
              <FormulateInput
                id="deleted_date"
                :value="purchaseRequisition.deletedDate"
                type="label"
                filter="datetime"
                :label="$t('Data da exclusão')"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                id="deleted_by"
                :value="purchaseRequisition.deletedBy ? purchaseRequisition.deletedBy.name : ''"
                type="label"
                :label="$t('Excluído por')"
              />
            </b-col>
            <b-col md="4">
              <FormulateInput
                id="deleted_reason"
                :value="purchaseRequisition.deletedReason"
                type="label"
                :label="$t('Motivo da exclusão')"
              />
            </b-col>
          </template>

          <template v-if="isEdit">
            <b-col md="4">
              <FormulateInput
                id="requested_date"
                :value="purchaseRequisition.requestedDate"
                type="label"
                filter="datetime"
                :label="$t('Data de criação')"
              />
            </b-col>
            <b-col md="4">
              <FormulateInput
                id="origin"
                :value="$t(`PurchaseRequisition.Origin.${purchaseRequisition.origin}`)"
                type="label"
                :label="$t('Origem da requisição')"
              />
            </b-col>
            <b-col md="4">
              <FormulateInput
                id="requested_by_user"
                :value="purchaseRequisition.requestedByUser ? purchaseRequisition.requestedByUser.name : ''"
                type="label"
                :label="$t('Usuário que requisitou')"
              />
            </b-col>
          </template>

          <b-col md="12">
            <FormulateInput
              id="observation"
              v-model="purchaseRequisition.observation"
              name="observation"
              :type="isObservationReadOnly ? 'label' : 'textarea'"
              :label="$t('Observação')"
              class="required"
              :validation="isObservationRequired ? 'required' : ''"
            />
          </b-col>
        </b-row>
      </FormulateForm>
    </b-card-actions>

    <template v-if="purchaseRequisition.storeId">
      <b-row>
        <b-col md="12">
          <b-card-actions
            :show-loading="saving"
            action-collapse
            :title="$t('Itens da Requisição')"
          >
            <order-items
              ref="orderItems"
              is-xml
              :purchase-requisition="purchaseRequisition"
              :can-manage="canEditItemsInSubmitToSupplier"
              @update-average-prices="onUpdateItemsAveragePrices"
              @add-item="onAddItem"
            />
          </b-card-actions>
        </b-col>
      </b-row>

      <product-sidebar
        ref="productSidebar"
        :store-id="purchaseRequisition.storeId"
      />
    </template>

    <template v-if="isEdit && purchaseRequisition.supplierOrders && purchaseRequisition.supplierOrders.length > 0">
      <b-row>
        <b-col md="12">
          <b-card-actions
            action-collapse
            :title="$t('Pedidos aos Fornecedores')"
          >
            <b-table
              responsive
              striped
              class="bordered"
              :fields="supplierOrderFields"
              :items="purchaseRequisition.supplierOrders"
            >
              <template #cell(requestedDate)="row">
                <template v-if="editingOrderIndex === row.index">
                  <!-- <b-form-input
                    v-model="row.item.requestedDate"
                    type="datepicker"
                    size="sm"
                  /> -->
                  <FormulateInput
                    :id="`requested_date_${index}`"
                    v-model="row.item.requestedDate"
                    class="w-100 required"
                    type="datepicker"
                    validation="required"
                  />
                </template>
                <template v-else>
                  {{ row.item.requestedDate | date }}
                </template>
              </template>
              <template #cell(orderNumber)="row">
                <template v-if="editingOrderIndex === row.index">
                  <b-form-input
                    v-model="row.item.orderNumber"
                    type="text"
                    size="sm"
                  />
                </template>
                <template v-else>
                  {{ row.item.orderNumber }}
                </template>
              </template>
              <template #cell(supplier)="row">
                <template v-if="editingOrderIndex === row.index">
                  <e-person-search
                    v-model="row.item.supplier"
                    is-supplier
                    value-is-object
                    :show-label="false"
                    class="w-100"
                    @input="onSupplierSelectedInline($event, row.index)"
                  />
                </template>
                <template v-else>
                  {{ row.item.supplier }}
                </template>
              </template>
              <template #cell(attachments)="row">
                <div v-if="row.item.attachments && row.item.attachments.length > 0">
                  <b-link
                    v-for="(url, index) in row.item.attachments"
                    :key="index"
                    :href="url"
                    target="_blank"
                  >
                    Anexo {{ index + 1 }}
                  </b-link>
                </div>
                <span v-else>-</span>
              </template>
              <template #cell(actions)="row">
                <template v-if="editingOrderIndex === row.index">
                  <div style="display: flex; gap: 5px;">
                    <e-button
                      title="Confirmar"
                      icon="check"
                      variant="success"
                      size="sm"
                      class="px-1"
                      @click="onConfirmEditSupplierOrder(row.index)"
                    />
                    <e-button
                      title="Cancelar"
                      icon="x"
                      variant="outline-secondary"
                      size="sm"
                      class="px-1"
                      @click="onCancelEditSupplierOrder()"
                    />
                  </div>
                </template>
                <template v-else>
                  <e-grid-actions
                    :show-update="!readOnly"
                    :show-delete="!readOnly"
                    @delete="onDeleteSupplierOrder(row.index)"
                    @update="onEditSupplierOrderInline(row.index)"
                  />
                </template>
              </template>
            </b-table>
          </b-card-actions>
        </b-col>
      </b-row>
    </template>

    <template v-if="isEdit && purchaseRequisition.purchaseOrders && purchaseRequisition.purchaseOrders.length > 0">
      <b-row>
        <b-col md="12">
          <b-card-actions
            action-collapse
            :title="$t('Pedidos de Compra Associados')"
          >
            <b-table
              responsive
              striped
              class="bordered"
              :fields="purchaseOrderFields"
              :items="purchaseRequisition.purchaseOrders"
            >
              <template #cell(purchaseOrderId)="row">
                <router-link
                  :to="purchaseOrderRoute(row.item)"
                  target="_blank"
                >
                  {{ row.item.purchaseOrderId }}
                </router-link>
              </template>
              <template #cell(status)="row">
                <e-status-badge
                  :status="row.item.status"
                  :options="mxPurchaseOrderStatusOptions()"
                />
              </template>
            </b-table>
          </b-card-actions>
        </b-col>
      </b-row>
    </template>

    <modal-cancel-purchase-requisition
      ref="modalCancel"
      @after-confirm="() => $router.back()"
    />

    <modal-submit-supplier-order
      ref="modalSubmitSupplierOrder"
      :purchase-requisition-id="purchaseRequisition.id || 0"
      @after-confirm="onSupplierOrderAdded"
    />

    <e-crud-actions
      :show-save="false"
      :busy="saving"
      :additional-actions="additionalActions"
      @saveDraft="onSaveDraft"
      @cancel="onCancelSubmit"
      @reevaluate="onSubmitOrder"
      @submit="onSubmitOrder"
      @completeRequisition="onCompleteRequisition"
      @cancelRequisition="onCancel"
      @submitSupplier="onSubmitSupplier"
    />
  </section>
</template>

<script>
import delegablePermissions from '@/utils/delegable-permissions'
import { BRow, BCol, BTable, BLink, BFormInput } from 'bootstrap-vue'
import EPersonSearch from '@/views/components/inputs/EPersonSearch.vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapState, mapGetters } from 'vuex'
import { purchaseOrderDomains, purchaseRequisitionDomains, uploader } from '@/mixins'
import { ECrudActions, EStoreCombo, EGridActions, EButton, EStatusBadge } from '@/views/components'
import { getApiErrorData, getApiErrorDataMessage } from '@/utils/error-utils'
import OrderItems from './components/OrderItems.vue'
import ModalCancelPurchaseRequisition from './components/ModalCancelPurchaseRequisition.vue'
import ModalSubmitSupplierOrder from './components/ModalSubmitSupplierOrder.vue'
import ProductSidebar from './components/ProductSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BLink,
    BFormInput,
    EButton,
    EPersonSearch,
    BCardActions,
    OrderItems,
    ECrudActions,
    EStoreCombo,
    EGridActions,
    EStatusBadge,
    ModalCancelPurchaseRequisition,
    ModalSubmitSupplierOrder,
    ProductSidebar,
  },

  mixins: [uploader, purchaseRequisitionDomains, purchaseOrderDomains],

  data() {
    return {
      mode: 'xml',
      loadingAveragePrices: false,

      uploading: false,
      saving: false,
      showAverageCostDiffWarning: false,
      delegateUserData: null,
      editingOrderIndex: null,
      originalOrderData: null,
    }
  },

  computed: {
    ...mapState('app', ['modalAuthRef']),
    ...mapGetters('pages/purchase/purchaseRequisition/purchaseRequisitionMaintain', [
      'readOnly',
      'approvalMessage',
    ]),
    ...mapState('pages/purchase/purchaseRequisition/purchaseRequisitionMaintain', [
      'purchaseRequisition',
      'lastApprovalFlow',
      'itemsPurchase',
    ]),
    ...mapGetters('app', ['storeOptions']),
    ...mapGetters('pages/catalog/products', ['getComboGroups']),
    /**
     * Além dos status já finalizados, o backend recusa alteração da observação quando a
     * requisição está liberada para envio ao fornecedor (ValidateSubmitToSupplierChanges).
     */
    isObservationReadOnly() {
      return (
        this.readOnly ||
        this.purchaseRequisition.status === 'Completed' ||
        this.isSubmitToSupplier
      )
    },
    /**
     * A observação é obrigatória apenas nas requisições manuais: as automáticas e as de
     * sugestão são geradas pelo sistema, que já preenche o campo com o motivo da geração.
     */
    isObservationRequired() {
      return !this.isObservationReadOnly && this.purchaseRequisition.origin === 'Manual'
    },
    isGroupReadOnly() {
      return (
        this.readOnly ||
        this.purchaseRequisition.status === 'Completed' ||
        this.isSubmitToSupplier
      )
    },
    purchaseRequisitionStatus() {
      return (
        this.$t(`PurchaseRequisition.Status.${this.purchaseRequisition.status}`) +
        (this.approvalMessage ? ` (${this.approvalMessage})` : '')
      )
    },
    approvalFlowRoute() {
      if (this.lastApprovalFlow) {
        return {
          name: 'approval-flow-details',
          params: {
            id: this.lastApprovalFlow.id,
          },
        }
      }
      return null
    },
    isEdit() {
      return !!this.purchaseRequisition.id
    },
    isSubmitToSupplier() {
      return this.purchaseRequisition.status === 'SubmitToSupplier'
    },
    canManagePurchaseRequisition() {
      return this.$can('Manage', 'PurchaseRequisition')
    },
    canEditItemsInSubmitToSupplier() {
      return this.canManagePurchaseRequisition
    },
    storeLabel() {
      return this.$options.filters.storeName(this.purchaseRequisition?.store)
    },
    additionalActions() {
      const { CHANGES_REQUESTED, REJECTED, DRAFT, SUBMIT_TO_SUPPLIER } = this.mxPurchaseRequisitionStatusEnum
      return [
        {
          name: 'saveDraft',
          icon: 'save',
          color: 'green',
          tooltip: this.$t('Salvar como rascunho'),
          show: !this.purchaseRequisition?.id || [DRAFT].includes(this.purchaseRequisition.status),
        },
        {
          name: 'completeRequisition',
          icon: 'check_circle',
          color: 'green',
          tooltip: this.$t('Finalizar requisição'),
          show:
            !!this.purchaseRequisition?.id &&
            this.mxCanCompletePurchaseRequisition(this.purchaseRequisition.status),
        },
        {
          name: 'cancelRequisition',
          icon: 'cancel',
          color: 'red',
          tooltip: this.$t('Cancelar requisição'),
          show:
            !!this.purchaseRequisition?.id &&
            !this.mxIsPurchaseRequisitionInFinalStatus(this.purchaseRequisition.status),
        },
        {
          name: 'reevaluate',
          icon: 'refresh',
          color: 'green',
          tooltip: this.$t('Submeter para reavaliação'),
          show: [CHANGES_REQUESTED].includes(this.purchaseRequisition.status),
        },
        {
          name: 'submit',
          icon: 'save',
          color: 'green',
          tooltip: this.$t('Submeter'),
          show: !this.readOnly && [REJECTED, DRAFT].includes(this.purchaseRequisition.status),
        },
        {
          name: 'submitSupplier',
          icon: 'send',
          color: 'orange',
          tooltip: this.$t('Submeter ao Fornecedor'),
          show: [SUBMIT_TO_SUPPLIER].includes(this.purchaseRequisition.status) && this.$can('SubmitToSupplier', 'PurchaseRequisition'),
        },
      ].filter(item => item.show !== false)
    },
    purchaseOrderFields() {
      return [
        {
          label: this.$t('Nº do Pedido'),
          key: 'purchaseOrderId',
          class: 'text-center',
          thStyle: { width: '120px' },
        },
        {
          label: this.$t('Status'),
          key: 'status',
          class: 'text-center',
          thStyle: { width: '170px' },
        },
        {
          label: this.$t('Fornecedor'),
          key: 'supplierName',
          formatter: value => value || '-',
        },
        {
          label: this.$t('Nº da Nota'),
          key: 'invoiceNumber',
          class: 'text-center',
          thStyle: { width: '120px' },
          formatter: value => value || '-',
        },
        {
          label: this.$t('Data da Nota'),
          key: 'invoiceDate',
          class: 'text-center',
          thStyle: { width: '130px' },
          formatter: value => (value ? this.$options.filters.date(value) : '-'),
        },
        {
          label: this.$t('Valor'),
          key: 'totalValue',
          tdClass: 'text-right',
          thStyle: { width: '150px' },
          formatter: value => this.$options.filters.currency(value),
        },
      ]
    },
    supplierOrderFields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'actions',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Data de solicitação'),
          key: 'requestedDate',
          class: 'text-center',
          thStyle: { width: '150px' },
        },
        {
          label: this.$t('Número do pedido'),
          key: 'orderNumber',
          thStyle: { width: '150px' },
        },
        {
          label: this.$t('Fornecedor'),
          key: 'supplier',
        },
        {
          label: this.$t('Anexos'),
          key: 'attachments',
          thStyle: { width: '200px' },
        },
      ]
    },
  },
  watch: {
    'purchaseRequisition.storeId': {
      handler(newVal, oldVal) {
        if (newVal === null || oldVal === null) {
          return
        }
        if (this.purchaseRequisition.loaded && oldVal !== undefined && newVal.toString() !== oldVal.toString()) {
          this.cleanSkuSearchResults()
          this.cleanItems()
        }
      },
    },

    /**
     * Mantém o filtro de produtos alinhado ao grupo da requisição, de modo que somente
     * produtos do grupo selecionado sejam listados para inclusão.
     */
    'purchaseRequisition.groupId': {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal === oldVal) {
          return
        }

        this.setFilterValue({ key: 'groupId', value: newVal || null })

        if (oldVal !== undefined) {
          this.cleanSkuSearchResults()
        }
      },
    },
  },

  created() {
    // if (this.$route.params?.id) this.cleanState()
  },

  async mounted() {
    if (this.getComboGroups.length === 0) {
      this.fetchProductCombos()
    }

    if (this.$route.params?.id) {
      const { CHANGES_REQUESTED, REJECTED, SUBMITTED, ISSUER_INVOICE_CANCELLED } =
        this.mxPurchaseRequisitionStatusEnum
      try {
        this.uploading = true
        await this.fetchPurchaseRequisition(this.$route.params?.id)
        if (
          [CHANGES_REQUESTED, REJECTED, SUBMITTED, ISSUER_INVOICE_CANCELLED].includes(
            this.purchaseRequisition.status
          )
        ) {
          await this.fetchPurchaseRequisitionLastApprovalFlow(this.$route.params?.id)
        }
      } catch (error) {
        this.showError({
          error,
          message: this.$t('Não foi possível obter os dados do Pedido de Compra'),
        })
        this.$router.back()
      } finally {
        this.uploading = false
      }
    }
  },

  methods: {
    ...mapActions('pages/purchase/purchaseRequisition', ['fetchManagementAccounts']),
    ...mapActions('pages/purchase/purchaseRequisition/purchaseRequisitionMaintain', [
      'savePurchaseRequisition',
      'reevaluatePurchaseRequisition',
      'fetchPurchaseRequisition',
      'fetchPurchaseRequisitionLastApprovalFlow',
      'cleanSkuSearchResults',
      'cleanItems',
      'resetPurchaseRequisition',
      'setFilterValue',
      'completePurchaseRequisition',
    ]),
    ...mapActions('pages/catalog/products', ['fetchProductCombos']),

    onAddItem() {
      if (this.isSubmitToSupplier && !this.canManagePurchaseRequisition) {
        this.showError({
          message: this.$t('Você não tem permissão para adicionar itens quando a requisição está em "Enviar ao Fornecedor"'),
        })
        return
      }
      this.$refs.productSidebar.show()
    },

    async onReevaluateOrder() {
      console.log('teste')
    },

    /**
     * Valida os dados da requisição (cabeçalho), onde fica a observação obrigatória
     * das requisições manuais.
     */
    validateRequisitionForm() {
      this.$refs.form.showErrors()
      return this.$refs.form.isValid
    },

    async onSaveDraft() {
      if (!this.validateRequisitionForm()) {
        return
      }

      try {
        this.saving = true
        const wasNew = !this.purchaseRequisition.id
        const result = await this.savePurchaseRequisition({ status: 'Draft' })
        
        if (wasNew && result?.id) {
          // Nova requisição criada, redireciona para a URL com o ID
          this.$router.push({
            name: 'purchase-requisition-maintain',
            params: { id: result.id },
          })
          this.showSuccess({ message: this.$t('Rascunho criado com sucesso') })
        } else if (result?.id) {
          // Requisição existente, recarrega os dados
          await this.fetchPurchaseRequisition(result.id)
          this.showSuccess({ message: this.$t('Rascunho salvo com sucesso') })
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    async onSubmitOrder() {
      if (!this.validateRequisitionForm()) {
        return
      }

      try {
        const confirmed = await this.confirm({
          title: this.$t('ATENÇÃO: Confirma envio?'),
          text: this.$t(
            'Após o envio, a requisição será enviada para aprovação da equipe responsável.'
          ),
        })

        if (confirmed) {
          if (this.$refs.orderItems.validateForm()) {
            this.showError({
              message: this.$t('A quantidade deve ser maior que zero'),
            })
            return
          }

          this.saving = true
          let data

          if (this.purchaseRequisition.status === 'ChangesRequested') {
            await this.reevaluatePurchaseRequisition()
          } else {
            data = await this.savePurchaseRequisition({ status: 'Submitted' })
          }

          this.actionsAfterSave(data)
        }
      } catch (error) {
        if (Array.isArray(error.response?.data?.Message)) {
          await this.confirmRevision(error.response?.data?.Message[0])
        } else {
          const errorData = getApiErrorData(error)
          if (errorData.message === 'PURCHASE_ORDER.MANUAL_ERROR.HAS_DEACTIVATED_ITEMS')
            this.showError({ message: getApiErrorDataMessage(errorData) })
          else this.showError({ error })
        }
      } finally {
        this.saving = false
      }
    },

    async confirmRevision(errorMessage) {
      const htmlMessage = this.mxGetAverageCostDiffAlertMsg(errorMessage)

      if (!this.showAverageCostDiffWarning) {
        await this.confirmHtml({
          title: this.$t('Atenção'),
          html: htmlMessage,
          confirmButtonText: this.$t('Ok'),
          showCancelButton: false,
        })
        this.showAverageCostDiffWarning = true
        return
      }

      try {
        const confirmed = await this.confirmHtml({
          title: this.$t('Atenção'),
          html: htmlMessage,
          confirmButtonText: this.$t('Prosseguir, eu já revisei'),
          cancelButtonText: this.$t('Revisar'),
          focusCancel: true,
        })

        if (confirmed) {
          this.delegateUserData = await this.modalAuthRef.auth(
            delegablePermissions.ERP_PURCHASE_ORDER_DIFF_AVERAGE_PRICE,
            this.purchaseRequisition?.store?.id
          )
          const data = await this.savePurchaseRequisition({
            itemsUnitValueRevision: true,
            tokenDelegated: this.delegateUserData?.token,
            file: this.invoiceXmlFile?.[0],
          })
          this.actionsAfterSave(data)
        }
      } catch (error) {
        if (error.name !== 'Cancel') {
          this.showError({ error })
        }
      }
    },

    async onCancelSubmit() {
      const isConfirmed =
        this.readOnly ||
        !this.purchaseRequisition.loaded ||
        (await this.confirm({
          title: this.$t('Deseja mesmo cancelar?'),
          text: this.$t('Os dados preenchidos serão perdidos'),
        }))
      if (isConfirmed) {
        this.$router.push({ name: 'purchase-requisition-list' })
      }
    },

    onCancel() {
      this.$refs.modalCancel.showModal(this.purchaseRequisition)
    },

    /**
     * Pedidos importados por XML/SEFAZ e pedidos manuais possuem telas distintas.
     */
    purchaseOrderRoute(purchaseOrder) {
      const { XML, SEFAZ } = this.PurchaseOrderTypeEnum
      const isXmlOrSefaz = [XML, SEFAZ].includes(purchaseOrder.type)

      return {
        name: isXmlOrSefaz ? 'purchase-order-details' : 'purchase-order-manual-details',
        params: { id: purchaseOrder.purchaseOrderId },
      }
    },

    async onCompleteRequisition() {
      const confirmed = await this.confirm({
        title: this.$t('ATENÇÃO: Confirma a finalização?'),
        text: this.$t(
          'Após finalizada, a requisição de compra não poderá mais ser alterada ou cancelada.'
        ),
      })

      if (!confirmed) {
        return
      }

      try {
        this.saving = true
        await this.completePurchaseRequisition(this.purchaseRequisition.id)
        await this.fetchPurchaseRequisition(this.purchaseRequisition.id)
        this.showSuccess({ message: this.$t('Requisição de compra finalizada com sucesso') })
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    actionsAfterSave(data) {
      if (typeof data?.message === 'string' && data?.message?.length > 0) {
        this.showWarning({ message: this.$t(data.message) })
      } else {
        this.showSuccess({ message: this.$t('Salvo com sucesso') })
      }
      this.$router.push({ name: 'purchase-requisition-list' })
    },

    async onUpdateItemsAveragePrices() {
      try {
        this.loadingAveragePrices = true

        this.loadItemsAveragePrice()
      } catch (e) {
        this.showWarning({
          message: this.$t('Não foi possível encontrar o preço e o preço de custo no ERP'),
        })
      } finally {
        this.loadingAveragePrices = false
      }
    },

    onSubmitSupplier() {
      this.$refs.modalSubmitSupplierOrder.showModal()
    },

    async onSupplierOrderAdded() {
      try {
        await this.fetchPurchaseRequisition(this.purchaseRequisition.id)
      } catch (error) {
        this.showError({
          error,
          message: this.$t('Não foi possível atualizar os dados da requisição'),
        })
      }
    },

    onEditSupplierOrderInline(index) {
      this.editingOrderIndex = index
      this.originalOrderData = JSON.parse(JSON.stringify(this.purchaseRequisition.supplierOrders[index]))
    },

    async onConfirmEditSupplierOrder(index) {
      try {
        this.saving = true
        await this.savePurchaseRequisition({})
        this.showSuccess({ message: this.$t('Pedido ao fornecedor atualizado com sucesso') })
        this.editingOrderIndex = null
        this.originalOrderData = null
      } catch (error) {
        this.showError({ error, message: this.$t('Erro ao atualizar pedido ao fornecedor') })
        // Restore original data on error
        if (this.originalOrderData) {
          Object.assign(this.purchaseRequisition.supplierOrders[index], this.originalOrderData)
        }
      } finally {
        this.saving = false
      }
    },

    onCancelEditSupplierOrder() {
      if (this.editingOrderIndex !== null && this.originalOrderData) {
        Object.assign(
          this.purchaseRequisition.supplierOrders[this.editingOrderIndex],
          this.originalOrderData
        )
      }
      this.editingOrderIndex = null
      this.originalOrderData = null
    },

    async onDeleteSupplierOrder(index) {
      const confirmed = await this.confirm({
        title: this.$t('Confirmar exclusão'),
        text: this.$t('Tem certeza que deseja deletar este pedido?'),
        confirmButtonText: this.$t('Deletar'),
        cancelButtonText: this.$t('Cancelar'),
      })

      if (confirmed) {
        try {
          this.saving = true
          this.purchaseRequisition.supplierOrders.splice(index, 1)
          await this.savePurchaseRequisition({})
          this.showSuccess({ message: this.$t('Pedido ao fornecedor removido com sucesso') })
        } catch (error) {
          this.showError({ error, message: this.$t('Erro ao remover pedido ao fornecedor') })
        } finally {
          this.saving = false
        }
      }
    },

    onSupplierSelectedInline(supplier, index) {
      if (supplier) {
        this.purchaseRequisition.supplierOrders[index].supplier = `${supplier.id} - ${supplier.name || supplier.companyName}`
      } else {
        this.purchaseRequisition.supplierOrders[index].supplier = null
      }
    },
  },
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: 0.5s;
}

/* .fade-leave-active below version 2.1.8 */
.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* .h-345 .card {
  min-height: 345px;
} */
</style>
